import * as React from 'react'
import { Link } from 'react-router-dom'
import { plantHref } from '@atbash-labs/plantview-links'
import type { PlantProcedureRef } from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'
import { SectionHeader } from '../entity/SectionHeader'
import { SessionEntityMark } from '../session-mark-ui'

export function ProceduresSection({
  procedures
}: {
  procedures: PlantProcedureRef[]
}): React.ReactElement | null {
  if (procedures.length === 0) return null

  return (
    <section>
      <SectionHeader
        label="Procedures"
        subtitle={`${procedures.length} ${procedures.length === 1 ? 'procedure' : 'procedures'}`}
      />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        <div className="flex flex-col gap-3">
          {procedures.map((procedure) => (
            <ProcedureRow key={procedure.id} procedure={procedure} />
          ))}
        </div>
      </div>
    </section>
  )
}

function ProcedureRow({ procedure }: { procedure: PlantProcedureRef }): React.ReactElement {
  const href = plantHref(procedure.id, 'Procedure', procedure)
  const metaBits: string[] = []
  if (procedure.frequency) metaBits.push(procedure.frequency)
  if (typeof procedure.duration_minutes === 'number') {
    metaBits.push(`${procedure.duration_minutes} min`)
  }

  const title = <span className="text-13 font-medium text-ink">{procedure.name}</span>

  return (
    <div className="flex min-w-0 items-start gap-2">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {href ? (
            <Link to={href} className="text-ink no-underline hover:text-brand hover:underline">
              {title}
            </Link>
          ) : (
            title
          )}
          {procedure.procedure_kind && (
            <Badge variant="outline" className="text-10">
              {procedure.procedure_kind}
            </Badge>
          )}
          {procedure.loto_required && (
            <Badge
              variant="outline"
              className="border-amber-400/40 bg-amber-500/10 text-10 text-amber-700 dark:text-amber-300"
            >
              LOTO
            </Badge>
          )}
        </div>
        {metaBits.length > 0 && (
          <div className="mt-0.5 text-12 text-ink-3">{metaBits.join(' · ')}</div>
        )}
      </div>
      <SessionEntityMark entityId={procedure.id} />
    </div>
  )
}
